import type React from "react";
import { type ReactNode, useState } from "react";

import {
  CircleAlertIcon,
  CircleCheckIcon,
  CircleQuestionMarkIcon,
} from "lucide-react";

import { cx, styled } from "simplydo/react/styled";
import {
  belowComputer,
  computerUp,
  mobileOnly,
  tabletUp,
} from "simplydo/react/theme";
import { H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Popup } from "simplydo/react/ui/popup";

export const OverviewContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${belowComputer} {
    padding: 1rem;
  }

  ${computerUp} {
    padding: 1rem 2rem;
  }

  h3 {
    color: gray;
    font-size: 1.7rem;
  }
  h1 {
    font-size: 1.9rem;
    font-weight: 700;
  }
  span {
    font-size: 1.1rem;
    color: #343434;
  }
  .action-buttons {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    .ui.button {
      &:not(:last-of-type) {
        margin-right: 0.5rem;
      }
    }
  }
`;

export const RowFlex = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

export const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 5px 0;

  ${mobileOnly} {
    flex-direction: column;
    max-width: 100%;
  }

  ${tabletUp} {
    flex-direction: row;
  }

  ${belowComputer} {
    max-width: 100%;
  }

  ${computerUp} {
    max-width: 50%;
  }

  img,
  i.icon {
    width: 100px;
    object-fit: contain;
    display: block;
    margin-right: 1rem;
  }

  ${mobileOnly} {
    img,
    i.icon {
      height: 50px;
    }
  }

  ${tabletUp} {
    img,
    i.icon {
      height: 100px;
    }
  }

  i.icon {
    position: relative;
    top: 21px;
  }
`;

export const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  h3,
  h1,
  span {
    display: block;
    margin: 0 0 0.25rem;
  }

  .meta {
    font-style: italic;
    font-size: 1.2rem;
    color: #5a5a5a;
  }
`;

export const UnlinkCompanyContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  > h5 {
    font-weight: normal;
    margin: 0;
  }
`;

export const AlternativeCompanyContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  > h5 {
    display: block;
    margin: 0;
  }
  > span {
    display: block;
    margin: 0;
    opacity: 0.8;
    font-size: 1rem;
    a {
      color: black;
      opacity: 0.8;
      text-decoration-line: underline;
    }
  }
  > .choose-alternate-option {
    display: flex;
    flex-direction: row;
    margin: 0.5rem 0;
    > .ui.button {
      width: 175px;
      height: 40px;
    }
  }
  .description {
    max-width: 50%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  .text {
    max-width: 30%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`;

export const AlternativeCompanyCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 5px 0;
  > .alt-company-info {
    display: flex;
    > img {
      height: 50px;
      width: 75px;
      border-radius: 5px;
      margin-right: 10px;
      object-fit: contain;
    }
    .alt-company-details {
      display: flex;
      flex-direction: column;
      justify-content: center;
      .alt-company-title {
        display: block;
      }
      .alt-company-description {
        display: block;
        opacity: 0.8;
      }
    }
  }
`;

const StyledInfoContainer = styled.div`
  display: flex;
  align-items: flex-start;
  max-width: 100%;

  ${belowComputer} {
    flex-direction: column;
  }

  ${computerUp} {
    flex-direction: row;
  }

  ${mobileOnly} {
    width: 100%;
  }

  &.is-wrapped {
    flex-flow: row wrap;
  }

  &.is-sectioned {
    margin-top: 10px;
    padding-top: 10px;
  }
`;

type InfoContainerProps = React.ComponentProps<typeof StyledInfoContainer> & {
  $wrapped?: boolean;
  $sectioned?: boolean;
  $compact?: boolean;
};

export const InfoContainer = ({
  $wrapped,
  $sectioned,
  className,
  ...props
}: InfoContainerProps) => (
  <StyledInfoContainer
    className={cx(
      $wrapped && "is-wrapped",
      $sectioned && "is-sectioned",
      className,
    )}
    {...props}
  />
);

const StyledInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;

  ${mobileOnly} {
    width: 100%;
  }

  &.is-wrapped {
    flex: 0.5;
  }

  &:not(.is-wrapped) {
    flex: 1;
  }

  > span,
  .meta {
    color: #343434;
    margin-bottom: 1rem;
  }
  > .meta {
    font-style: italic;
  }
  > h1 {
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: 1rem;
  }

  &:not(:last-of-type) {
    ${computerUp} {
      padding-right: 1rem;
      margin-right: 1rem;
      border-right: 1px solid lightgray;
    }
  }

  .boxes {
    display: flex;
    margin-top: 1rem;

    ${mobileOnly} {
      flex-direction: column;
      width: 100%;
    }

    ${tabletUp} {
      flex-direction: row;
    }
  }

  .attribution {
    color: gray;
    > a {
      color: gray;
      font-weight: 600;
      text-decoration-line: underline;
    }
  }
`;

type InfoBoxProps = React.ComponentProps<typeof StyledInfoBox> & {
  $wrapped?: boolean;
};

export const InfoBox = ({ $wrapped, className, ...props }: InfoBoxProps) => (
  <StyledInfoBox
    className={cx($wrapped && "is-wrapped", className)}
    {...props}
  />
);

const StyledInfoItem = styled.a`
  display: block;

  ${belowComputer} {
    flex: 0 100%;
  }

  ${computerUp} {
    &.is-compact {
      flex: 0 calc(33% - 0.5rem);

      &:not(:nth-of-type(3n)) {
        margin-right: 1rem;
      }
    }

    &:not(.is-compact) {
      flex: 0 calc(50% - 0.5rem);

      &:not(:nth-of-type(2n)) {
        margin-right: 1rem;
      }
    }
  }
`;

type InfoItemProps = React.ComponentProps<typeof StyledInfoItem> & {
  $compact?: boolean;
};

export const InfoItem = ({ $compact, className, ...props }: InfoItemProps) => (
  <StyledInfoItem
    className={cx($compact && "is-compact", className)}
    target="_blank"
    rel="noopener noreferrer"
    {...props}
  />
);

const StyledInfoBoxWidget = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  max-width: 100%;
  margin: 10px 0;

  ${mobileOnly} {
    width: 100%;
  }

  &:not(:first-of-type) {
    ${computerUp} {
      margin-left: 20px;
    }
  }

  &.has-box {
    -webkit-box-shadow: 2px 2px 6px 2px #d3d3d3;
    box-shadow: 2px 2px 6px 2px #d3d3d3;
    border-radius: 5px;

    ${mobileOnly} {
      padding: 15px;
    }

    ${tabletUp} {
      padding: 20px;
    }
  }

  &.is-relaxed {
    padding: 0;
  }

  &.is-sectioned {
    margin-top: 10px;
    padding-top: 10px;
  }

  > h3 {
    color: #000;
  }
  p {
    font-size: 1.2em;
  }
  span {
    font-size: 1em;
  }
  .attribution {
    color: gray;
    > a {
      color: gray;
      font-weight: 600;
      text-decoration-line: underline;
    }
  }
`;

type InfoBoxWidgetProps = React.ComponentProps<typeof StyledInfoBoxWidget> & {
  $noBox?: boolean;
  $relaxed?: boolean;
  $sectioned?: boolean;
  $boxed?: boolean;
};

export const InfoBoxWidget = ({
  $noBox,
  $relaxed,
  $sectioned,
  $boxed,
  className,
  ...props
}: InfoBoxWidgetProps) => (
  <StyledInfoBoxWidget
    className={cx(
      !$noBox && "has-box",
      $relaxed && "is-relaxed",
      $sectioned && "is-sectioned",
      $boxed && "has-box",
      className,
    )}
    {...props}
  />
);

export const InfoBoxWidgetTitleArea = styled.div<{
  $alignItems?: string;
  $noMargin?: boolean;
  $flexDirection?: string;
}>`
  display: flex;
  flex-wrap: wrap;
  align-items: ${({ $alignItems }) => $alignItems || "center"};
  gap: 10px;
  flex-direction: ${({ $flexDirection }) => $flexDirection || "row"};
  margin-bottom: 15px;
  h3 {
    color: #000;
    display: block;
    margin: 0 5px 0 0;
    line-height: 0.9em;
  }
  span {
    display: block;
    color: gray;
  }
  > i.icon {
    font-size: 1.5em;
    margin-right: 10px;
  }
`;

export const ContactInfoWidget = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: gray;
  margin-bottom: 1rem;
  word-break: break-all;
  overflow: hidden;
  overflow-wrap: break-word;
  i.icon {
    margin-right: 0.75rem;
    display: block;
  }
  span.icon-alt {
    font-weight: bold;
    margin-right: 1.15rem;
    font-size: 1.4rem;
    margin-left: 6px;
  }
  a,
  span {
    color: #343434;
    display: block;
  }
  a {
    text-decoration-line: underline;
  }
  .contact-info-meta {
    opacity: 0.7;
    display: flex;
    flex-direction: row;
    align-items: center;
    > span {
      margin-bottom: 0;
      font-size: 0.85rem;
    }
    > i.icon {
      margin-left: 2.5px;
    }
  }
`;

export const MetaContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  align-self: stretch;
  margin-top: 1rem;
  span {
    color: gray;
    a {
      color: gray;
      text-decoration-line: underline;
    }
  }
  .action-buttons {
    display: flex;
    align-items: center;
    flex-direction: row;
  }
  .ui.button {
    height: 36px;
    &:not(:last-of-type) {
      margin-right: 1rem;
    }
  }
  .ui.dropdown {
    margin-right: 1rem;
    max-width: 50%;
  }
  > .ui.active.loader {
    position: relative;
  }
  > .ui.loader::after {
    border-color: #767676 transparent transparent !important;
    margin-left: 5px;
  }
`;

const StyledKeyFact = styled.div`
  display: flex;
  flex: 0 1 50%;
  margin-bottom: 1rem;

  &.has-image {
    flex-direction: row;
    align-items: center;
  }

  &:not(.has-image) {
    flex-direction: column;
    align-items: flex-start;
  }

  &.is-clickable {
    cursor: pointer;
  }

  img {
    height: 50px;
    width: 50px;
    object-fit: contain;
    margin-right: 7.5px;
    border-radius: 5px;
  }
  > .sub-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
  h5 {
    font-size: 1.2rem;
    margin-bottom: 0.25rem;
  }
  span,
  a {
    font-size: 1.1rem;
  }
  .info {
    text-decoration-line: underline;
    color: gray;
    font-size: 1rem;
    margin-top: 0.5rem;
  }
  .fact {
    margin-top: 2.5px;
    font-size: 1rem;

    &.is-underlined {
      text-decoration-line: underline;
      font-weight: bold;
    }
  }
`;

export const StyledKeyFactLink = styled(StyledKeyFact)`
  display: flex;
  flex-direction: row;

  border-radius: 5px;
  padding: 5px;
  cursor: pointer;

  &:hover {
    background-color: #f1f1f1;
  }
  h5 {
    font-weight: 400;
  }
  span {
    color: gray;
    font-size: 1rem;
  }
  .fake-link {
    color: #000;
    text-decoration-line: none;
  }
  .subtitle {
    color: #000;
  }
  .fact {
    margin-top: 5px;
    font-size: 0.9rem;
  }
  > .image-container {
    min-width: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 5px;
    > * {
      display: block;
    }
  }
`;

export const KeyFactLinkSeeMore = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  > a,
  span {
    text-decoration-line: underline;
    color: gray;
  }
  .ui.dimmer .ui.loader::after {
    border-color: gray transparent transparent !important;
  }
  .ui.loader::after {
    border-color: #767676 transparent transparent !important;
  }
`;

const AcquireSpanStyled = styled.span`
  font-weight: bold;
  font-size: 1.3em !important;

  > span.is-clickable {
    font-size: 1em !important;
    color: #5e5ef8;
    cursor: pointer;
  }
`;

type AcquireSpanProps = React.ComponentProps<typeof AcquireSpanStyled> & {
  $canClick?: boolean;
};

export const AcquireSpan = ({
  $canClick,
  children,
  className,
  ...props
}: AcquireSpanProps) => (
  <AcquireSpanStyled className={className} {...props}>
    <span className={cx($canClick && "is-clickable")}>{children}</span>
  </AcquireSpanStyled>
);

type KeyFactProps = {
  title: string | ReactNode;
  fact: string | ReactNode;
  info?: string;
  verified?: boolean;
  notVerified?: boolean;
  noEllipsis?: boolean;
  underlined?: boolean;
  onClick?: () => void;
  style?: React.CSSProperties;
};

export const KeyFact = ({
  title,
  fact,
  info,
  verified,
  notVerified,
  noEllipsis,
  underlined,
  onClick,
  style,
}: KeyFactProps) => (
  <StyledKeyFact
    className={cx(onClick && "is-clickable")}
    onClick={onClick}
    style={style}
  >
    <H5>
      {title}
      {!noEllipsis ? "" : ""}
    </H5>
    <span className={cx("fact", underlined && "is-underlined")}>
      {(verified || notVerified) && (
        <Icon
          icon={verified ? CircleCheckIcon : CircleAlertIcon}
          color={verified ? "green" : "orange"}
          style={{ marginRight: 2.5 }}
        />
      )}
      {fact}
    </span>
    {info && (
      <Popup
        trigger={
          <span className="info">
            <Icon icon={CircleQuestionMarkIcon} />
            What is this?
          </span>
        }
      >
        {info}
      </Popup>
    )}
  </StyledKeyFact>
);

type KeyFactLinkProps = {
  title: string;
  subtitle?: string;
  fact: string;
  url?: string;
  image?: string;
  imageAltContent?: ReactNode;
  style?: React.CSSProperties;
};

export const KeyFactLink = ({
  title,
  subtitle,
  fact,
  url,
  image,
  imageAltContent,
  ...rest
}: KeyFactLinkProps) => {
  const [imageDidError, setImageDidError] = useState(false);
  return (
    <StyledKeyFactLink
      className={cx(image && "has-image")}
      onClick={() => (url ? window.open(url, "_blank") : {})}
      {...rest}
    >
      {image && (
        <div className="image-container">
          {image && (!imageDidError || !imageAltContent) ? (
            <img onError={() => setImageDidError(true)} src={image} alt="" />
          ) : (
            imageAltContent
          )}
        </div>
      )}
      <div className="sub-container">
        <span className="fake-link">{title}</span>
        {subtitle && <span className="subtitle">{subtitle}</span>}
        <span className="fact">{fact}</span>
      </div>
    </StyledKeyFactLink>
  );
};
